import MenuButton from "@components/ui/buttons/MenuButton";
import SideMenuButton from "@navigation/SideMenuButton";
import Icon1 from "@assets/icons/Icon";
import { MOBILITY_LABELS } from "@components/ui/RentalVehicleBadge";
import { ordresApi } from "@services/api/ordres";
import { useOrdreStore } from "@store/ordreStore";
import { usePlanningStore } from "@store/planningStore";
import { useAtelierStore } from "@store/atelierStore";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { ORDRE_FLAG_CONFIG, ORDRE_FLAGS } from "types/ordre";
import { isOnServiceBox } from "@utils/serviceboxTransfer";
import type { Ordre, OrdreFlag } from "types/ordre";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";
import type { Payment } from "types/payment";


/**
 * Shared "Actions" menu for an ordre, used both on the Ordre detail page and in
 * the OrdreModal (opened from the planning). The menu markup is the single
 * source of truth so both stay identical; each host renders the sub-modals the
 * actions open and passes the corresponding callbacks.
 *
 * `context="modal"` hides actions that are redundant/page-only when the ordre is
 * already open as a modal from the planning (edit, navigate to this planning).
 */
export interface OrdreActionsMenuProps {
  ordre: Ordre;
  orgData: any;
  context?: "page" | "modal";
  closeRef?: React.MutableRefObject<(() => void) | null>;
  payments: Payment[];
  rentalVehicle: Vehicle | null;
  /** Toutes les mobilités de l'ordre, la plus ancienne d'abord. Un ordre peut enchaîner
   *  un véhicule de prêt puis un véhicule de location : elles ont le droit de se suivre,
   *  pas de se chevaucher. `ordre.rental_reservation_id` n'en désigne qu'une, la courante. */
  mobilities?: { reservation: VehicleReservation; vehicle: Vehicle | null }[];
  hasFacture: boolean;
  serviceboxProxyAvailable: boolean;
  sbEstimationLoading: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onRequestFlagReason: (flag: OrdreFlag, newFlags: OrdreFlag[]) => void;
  /** Appele apres chaque mise a jour faite ICI (drapeaux, carte grise...). Les hotes
   *  qui ne lisent pas le store — la modale ouverte depuis le planning — s'en servent
   *  pour rafraichir leur copie locale au lieu d'attendre une reouverture. */
  onOrdreUpdated?: (updated: Ordre) => void;
  onShowReminder: () => void;
  onAddDocument: () => void;
  onAddFacture: () => void;
  onServiceBoxTransfer: () => void;
  /** Report interventions added in Carvis onto the RDV ServiceBox already holds. Distinct
   *  from a transfer: a transfer CREATES a RDV, and re-running it on an ordre that is
   *  already there asks ServiceBox for a duplicate. */
  onPushInterventions?: () => void;
  /** Origin (ServiceBox or Carvis) + the recorded ServiceBox exchanges for this ordre. */
  onShowSyncHistory?: () => void;
  onImportEstimation: () => void;
  onShowSecondary: () => void;
  onShowRental: () => void;
  onRemoveRental: () => void;
  onEditReservation?: () => void;
  /** Ouvre / retire UNE mobilité précise. Sans eux, le menu retombe sur les callbacks
   *  mono-véhicule (onEditReservation / onRemoveRental). */
  onEditMobility?: (reservationId: string) => void;
  onRemoveMobility?: (reservationId: string) => void;
  onShowContrat: () => void;
  onShowTracking: () => void;
  onShowPayment: (type: "acompte" | "solde") => void;
  onRecordManualPayment?: () => void;
}

export default function OrdreActionsMenu({
  ordre,
  orgData,
  context = "page",
  closeRef,
  payments,
  rentalVehicle,
  mobilities,
  hasFacture,
  serviceboxProxyAvailable,
  sbEstimationLoading,
  onEdit,
  onDelete,
  onRequestFlagReason,
  onOrdreUpdated,
  onShowReminder,
  onAddDocument,
  onAddFacture,
  onServiceBoxTransfer,
  onPushInterventions,
  onShowSyncHistory,
  onImportEstimation,
  onShowSecondary,
  onShowRental,
  onRemoveRental,
  onEditReservation,
  onEditMobility,
  onRemoveMobility,
  onShowContrat,
  onShowTracking,
  onShowPayment,
  onRecordManualPayment,
}: OrdreActionsMenuProps) {
  const isModal = context === "modal";
  // Demandes d'acompte / de solde (Bridge) : rouvertes à toute l'équipe le 16/10/2026.
  const paymentRequestsEnabled = true;

  const dayLabel = (iso?: string) => {
    if (!iso) return "";
    const [, m, d] = iso.split("-");
    return m && d ? `${d}/${m}` : iso;
  };
  const todayIso = new Date().toISOString().slice(0, 10);
  // L'hôte qui ne passe pas `mobilities` (ancien appel) garde l'affichage mono-véhicule.
  const mobilityRows = mobilities
    ? mobilities.map(({ reservation, vehicle }) => ({
        id: reservation.id,
        past: (reservation.endDate || reservation.startDate || "") < todayIso,
        label: `${vehicle ? `${vehicle.name} (${vehicle.plate})` : "Véhicule attribué"} · ${dayLabel(reservation.startDate)} → ${dayLabel(reservation.endDate)}`,
      }))
    : ordre.rental_vehicle_id
      ? [{
          id: ordre.rental_reservation_id || "",
          past: false,
          label: rentalVehicle ? `${rentalVehicle.name} (${rentalVehicle.plate})` : "Véhicule attribué",
        }]
      : [];

  const handleEditMobility = (id: string) =>
    onEditMobility ? onEditMobility(id) : onEditReservation?.();
  const handleRemoveMobility = (id: string) =>
    onRemoveMobility ? onRemoveMobility(id) : onRemoveRental();

  const handleToggleFlag = async (flag: OrdreFlag) => {
    try {
      const currentFlags = ordre.flags || [];
      const isActive = currentFlags.includes(flag);
      const newFlags = isActive ? currentFlags.filter((f) => f !== flag) : [...currentFlags, flag];
      if (!isActive && !ordre.flag_reason) {
        onRequestFlagReason(flag, newFlags as OrdreFlag[]);
        return;
      }
      const updated = await ordresApi.update(ordre.id, {
        flags: newFlags,
        ...(newFlags.length === 0 ? { flag_reason: "" } : {}),
      } as any);
      useOrdreStore.getState().updateOrdre(updated);
      onOrdreUpdated?.(updated);
      showNotification({ type: "success", message: isActive ? "Drapeau retiré" : `Drapeau « ${ORDRE_FLAG_CONFIG[flag].label} » ajouté` });
    } catch {
      showNotification({ type: "error", message: "Erreur" });
    }
  };

  const handleToggleCarteGriseRequired = async () => {
    try {
      const next = !ordre.carte_grise_not_required;
      const updated = await ordresApi.update(ordre.id, { carte_grise_not_required: next } as any);
      useOrdreStore.getState().updateOrdre(updated);
      onOrdreUpdated?.(updated);
      showNotification({
        type: "success",
        message: next
          ? "Dépôt de la carte grise masqué pour le client"
          : "Dépôt de la carte grise réaffiché pour le client",
      });
    } catch {
      showNotification({ type: "error", message: "Erreur" });
    }
  };

  return (
    <MenuButton label="Actions" closeRef={closeRef}>
      <div className="grid grid-cols-2" style={{ minWidth: 520 }}>
        {/* ── Column 1 ── */}
        <div className="flex flex-col">
          <div className="flex flex-col p-1">
            <div className="text-h2 text-neutral-400 p-2">ACTIONS RAPIDES</div>
            {!isModal && (
              <SideMenuButton size="large" label="Modifier l'ordre" name="modify" onClick={onEdit} />
            )}
            <SideMenuButton size="large" label="Supprimer l'ordre" name="delete" color="#ef4444" onClick={onDelete} />
            <div className="border-t border-neutral-100 my-1" />
            <div className="text-h2 text-neutral-400 p-2">DRAPEAUX</div>
            {ORDRE_FLAGS.map((flag) => {
              const cfg = ORDRE_FLAG_CONFIG[flag];
              const isActive = (ordre.flags || []).includes(flag);
              return (
                <SideMenuButton
                  key={flag}
                  size="large"
                  label={isActive ? `Retirer « ${cfg.label} »` : cfg.label}
                  name={cfg.icon}
                  color={isActive ? cfg.color : undefined}
                  onClick={() => handleToggleFlag(flag)}
                />
              );
            })}
          </div>
          <div className="flex flex-col border-t-1 p-1">
            <div className="text-h2 text-neutral-400 p-2">DOSSIER</div>
            {!isModal && ordre.dossier_id && (
              <SideMenuButton
                size="large"
                label="Voir le dossier"
                name="folder"
                onClick={() => navigateTo({ route: `/dossier/${ordre.dossier_id}` })}
              />
            )}
            {ordre.devis_sign_status === "pending" ? (
              <SideMenuButton
                size="large"
                label="Copier le lien de signature"
                name="link"
                onClick={() => {
                  if (ordre.devis_sign_token) {
                    const url = `${window.location.origin}/sign/${ordre.devis_sign_token}`;
                    navigator.clipboard.writeText(url);
                    showNotification({ type: "success", message: "Lien de signature copié" });
                  }
                }}
              />
            ) : ordre.devis_sign_status === "signed" ? (
              <SideMenuButton size="large" label="Devis signé" name="done2" color="#059669" />
            ) : null}
            {ordre?.intervention_groups && Object.values(ordre.intervention_groups).some((g) => g.devis_token) && (
              <SideMenuButton size="large" label="Rappels devis" name="cloche" onClick={onShowReminder} />
            )}
            <SideMenuButton size="large" label="Ajouter un document" name="upload" onClick={onAddDocument} />
            {hasFacture ? (
              <SideMenuButton size="large" label="Facture ajoutée" name="done2" color="#059669" />
            ) : (
              <SideMenuButton size="large" label="Ajouter la facture" name="file" onClick={onAddFacture} />
            )}
          </div>
          {orgData?.servicebox?.enabled && (
            <div className="flex flex-col border-t-1 p-1">
              <div className="text-h2 text-neutral-400 p-2">SERVICE BOX</div>
              {/* Créneau que ServiceBox a refusé d'enregistrer. Carvis a raison et n'y
                  reviendra pas tout seul (sinon c'est un mail par heure) : sans cette
                  pastille, l'atelier croirait les deux plannings d'accord. */}
              {ordre.sb_rdv_push_blocked?.error && (
                <div
                  className="flex flex-row items-start gap-2 px-3 py-2 mx-1 mb-1 rounded-lg"
                  style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}
                  title={ordre.sb_rdv_push_blocked.error}
                >
                  <Icon1 name="info" size={14} color="#991B1B" />
                  <span className="text-h4 font-medium" style={{ color: "#991B1B" }}>
                    Créneau non synchronisé
                    <span className="block font-normal" style={{ color: "#B91C1C" }}>
                      {ordre.sb_rdv_push_blocked.error}
                    </span>
                  </span>
                </div>
              )}
              {/* An ordre ServiceBox already holds is never "transferred" again — a transfer
                  creates a RDV and would ask for a duplicate. Everything Carvis can still
                  send it (interventions, numéro d'OR) goes through one screen, so the garage
                  has one intention to express instead of one button per mechanism. */}
              {isOnServiceBox(ordre) && onPushInterventions ? (
                <SideMenuButton
                  size="large"
                  label="Mettre à jour ServiceBox"
                  name="sett-servicebox"
                  onClick={onPushInterventions}
                />
              ) : (
                <SideMenuButton
                  size="large"
                  label={ordre.or_number ? `OR n°${ordre.or_number}` : "Transférer vers ServiceBox"}
                  name="sett-servicebox"
                  color={ordre.or_number ? "#059669" : undefined}
                  disabled={!serviceboxProxyAvailable}
                  onClick={onServiceBoxTransfer}
                />
              )}
              {onShowSyncHistory && (
                <SideMenuButton
                  size="large"
                  label="Historique du RDV"
                  name="eye"
                  onClick={onShowSyncHistory}
                />
              )}
              <SideMenuButton
                size="large"
                label={sbEstimationLoading ? "Récupération..." : "Récupérer devis"}
                name="import-devis"
                disabled={!serviceboxProxyAvailable || !ordre.or_number || sbEstimationLoading}
                onClick={onImportEstimation}
              />
              {ordre.or_number && (
                <>
                  {ordre.alpha_or_number && (
                    <SideMenuButton size="large" label={`Alpha DMS OR n°${ordre.alpha_or_number}`} name="sett-servicebox" color="#059669" disabled />
                  )}
                  {ordre.servicebox_dossier_id && (
                    <SideMenuButton
                      size="large"
                      label="Voir le Dossier ServiceBox"
                      name="side-reception"
                      onClick={() => {
                        window.open(
                          `https://servicebox.mpsa.com/panier/panierSetCurrent.do?id=${ordre.servicebox_dossier_id}`,
                          "_blank",
                        );
                      }}
                    />
                  )}
                  {/* A re-transfer CREATES a RDV. Offering it on an ordre ServiceBox already
                      holds asks for a duplicate — refused as "CCS indisponible" when the slot
                      is its own, and silently duplicated when the slot has since moved. The id
                      lives in `sb_agenda_rdv_id` on the ~99 % of ordres that came FROM
                      ServiceBox, so this must not test `servicebox_rdv_id` alone. */}
                  {!isOnServiceBox(ordre) && (
                    <SideMenuButton
                      size="large"
                      label="Re-transférer vers ServiceBox"
                      name="sett-servicebox"
                      disabled={!serviceboxProxyAvailable}
                      onClick={onServiceBoxTransfer}
                    />
                  )}

                </>
              )}
            </div>
          )}
        </div>
        {/* ── Column 2 ── */}
        <div className="flex flex-col border-l border-neutral-200">
          <div className="flex flex-col p-1">
            <div className="text-h2 text-neutral-400 p-2">PLANNING / RDV</div>
            <SideMenuButton size="large" label="Ajouter un RDV secondaire" name="plus" onClick={onShowSecondary} />
            {!isModal && ordre.reception_date && (
              <SideMenuButton
                size="large"
                label="Voir dans planning réception"
                name="side-reception"
                onClick={() => {
                  const rd = ordre.reception_date;
                  if (!rd) return;
                  const d = new Date(rd.seconds * 1000);
                  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  usePlanningStore.getState().setSelectedDate(iso);
                  usePlanningStore.getState().setHighlightOrdreId(ordre.id);
                  navigateTo({ route: "/reception" });
                }}
              />
            )}
            {!isModal && (
              <SideMenuButton
                size="large"
                label="Voir dans planning atelier"
                name="side-atelier"
                onClick={() => {
                  const jobs = useAtelierStore.getState().jobs.filter((j) => j.ordre_id === ordre.id);
                  if (jobs.length > 0) {
                    useAtelierStore.getState().setSelectedDate(jobs[0].date);
                  } else if (ordre.reception_date) {
                    const d = new Date(ordre.reception_date.seconds * 1000);
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    useAtelierStore.getState().setSelectedDate(iso);
                  }
                  useAtelierStore.getState().setHighlightOrdreId(ordre.id);
                  navigateTo({ route: "/atelier" });
                }}
              />
            )}
          </div>
          <div className="flex flex-col border-t-1 p-1">
            <div className="text-h2 text-neutral-400 p-2">VÉHICULE DE REMPLACEMENT</div>
            {ordre.mobility_option && mobilityRows.length === 0 && (
              <div
                className="flex flex-row items-center gap-2 px-3 py-2 mx-1 mb-1 rounded-lg"
                style={{
                  background: ordre.mobility_option === "attente" ? "#F3F4F6" : "#FEF3C7",
                  border: `1px solid ${ordre.mobility_option === "attente" ? "#D1D5DB" : "#FDE68A"}`,
                }}
              >
                <Icon1 name={ordre.mobility_option === "attente" ? "horloge" : "info"} size={14} color={ordre.mobility_option === "attente" ? "#6B7280" : "#92400E"} />
                <span className="text-h4 font-medium" style={{ color: ordre.mobility_option === "attente" ? "#6B7280" : "#92400E" }}>
                  {ordre.mobility_option === "attente" ? "Client en attente sur place" : `Demande : ${MOBILITY_LABELS[ordre.mobility_option] || ordre.mobility_option}`}
                </span>
              </div>
            )}
            {/* Une ligne PAR mobilité : le prêt du 1 au 8 puis la location du 8 au 15 sont
                deux réservations, et n'en montrer qu'une laissait croire à une erreur. La
                liste retombe sur le couple rental_* de l'ordre tant que l'hôte ne la
                fournit pas. */}
            {mobilityRows.length > 0 ? (
              <>
                {mobilityRows.map((row) => (
                  <div key={row.id} className="flex flex-row items-center">
                    <div className="flex-1 min-w-0">
                      <SideMenuButton
                        size="large"
                        label={row.label}
                        name="done2"
                        color={row.past ? "#9CA3AF" : "#059669"}
                        onClick={row.id ? () => handleEditMobility(row.id) : undefined}
                      />
                    </div>
                    {row.id && (
                      <button
                        onClick={() => handleRemoveMobility(row.id)}
                        title="Retirer cette mobilité"
                        className="p-2 mr-1 rounded-lg hover:bg-neutral-100"
                      >
                        <Icon1 name="delete" size={16} color="#ef4444" />
                      </button>
                    )}
                  </div>
                ))}
                <SideMenuButton size="large" label="Ajouter une mobilité" name="side-vehicules" onClick={onShowRental} />
                <SideMenuButton size="large" label="Générer contrat de prêt" name="document" color="#6366f1" onClick={onShowContrat} />
              </>
            ) : (
              <SideMenuButton size="large" label="Attribuer un véhicule" name="side-vehicules" onClick={onShowRental} />
            )}
          </div>
          <div className="flex flex-col border-t-1 p-1">
            <div className="text-h2 text-neutral-400 p-2">CLIENT</div>
            <SideMenuButton size="large" label="Voir le suivi client" name="eye" onClick={onShowTracking} />
            <SideMenuButton
              size="large"
              label={ordre.carte_grise_not_required ? "Réafficher le dépôt carte grise" : "Masquer le dépôt carte grise"}
              name="document"
              color={ordre.carte_grise_not_required ? "#059669" : undefined}
              onClick={handleToggleCarteGriseRequired}
            />
            <SideMenuButton
              size="large"
              label="Copier le lien de suivi"
              name="link"
              onClick={() => {
                const url = `${window.location.origin}/tracking/${ordre.id}`;
                navigator.clipboard.writeText(url);
                showNotification({ type: "success", message: "Lien copié !" });
              }}
            />
            {!payments.some((p) => p.type === "acompte" && !["cancelled", "error"].includes(p.state)) && (
              <SideMenuButton size="large" label="Demander un acompte" name="depot" onClick={() => onShowPayment("acompte")} disabled={!paymentRequestsEnabled} />
            )}
            {!payments.some((p) => p.type === "solde" && !["cancelled", "error"].includes(p.state)) &&
              !payments.some((p) => p.type === "acompte" && !["cancelled", "error", "paid"].includes(p.state)) && (
                <SideMenuButton size="large" label="Demander le solde" name="recup-argent" onClick={() => onShowPayment("solde")} disabled={!paymentRequestsEnabled} />
              )}
            {onRecordManualPayment && (
              <SideMenuButton size="large" label="Enregistrer un paiement" name="payments" onClick={onRecordManualPayment} />
            )}
          </div>
        </div>
      </div>
    </MenuButton>
  );
}
